"use client"

import React from 'react'

import { Header } from "../components/Header"
import { BottomNavigation } from "../components/BottomNavigation"
import { useServicePalette } from "../hooks/useServicePalette"
import { LoadingSpinner } from "../components/LoadingSpinner"
import { ErrorMessage } from "../components/ErrorMessage"
import { Clock, CheckCircle2, Phone } from "lucide-react"
import { useDashboard } from "../hooks/useDashboard"

// Importar nuevos componentes
import { OrderCard } from "../components/dashboard/OrderCard"
import { OrderCardSkeleton } from "../components/dashboard/OrderCardSkeleton"
import { PendingCollectionOrderCard } from "../components/dashboard/PendingCollectionOrderCard"
import { RestaurantStatusBanner } from "../components/dashboard/RestaurantStatusBanner"
import { DashboardHero } from "../components/dashboard/DashboardHero"
import { AccountSummary } from "../components/dashboard/AccountSummary"
import { DashboardActionButtons } from "../components/dashboard/DashboardActionButtons"
import { DashboardModals } from "../components/dashboard/DashboardModals"
// import { AbandonedOrdersScreen } from "../components/AbandonedOrdersScreen"
// import type { AbandonedOrder } from "../types"

export function DashboardPage() {
  const palette = useServicePalette()
  // const [showAbandonedOrdersScreen, setShowAbandonedOrdersScreen] = React.useState(true)
  const {
    routeId,
    isTakeAway,
    isSessionClosed,
    orderTiming,
    unpaidOrders,
    offers,
    loading,
    error,
    showOrderSuccess,
    waiterCalled,
    showWaiterModal,
    setShowWaiterModal,
    showCancelWaiterModal,
    setShowCancelWaiterModal,
    showChatbot,
    setShowChatbot,
    config,
    isOpen,
    isAccordionOpen,
    setIsAccordionOpen,
    showScheduledSkeleton,
    showErrorModal,
    setShowErrorModal,
    errorModalMessage,
    showPendingCollectionOrderModal,
    setShowPendingCollectionOrderModal,
    showPendingCollectionPaymentModal,
    setShowPendingCollectionPaymentModal,
    loadData,
    loadUnpaidOrders,
    handleCallWaiter,
    confirmCallWaiter,
    handleCancelWaiter,
    handleOpenChatbot,
    handleOrderClick,
    handlePaymentClick,
    confirmPendingCollectionOrder,
  } = useDashboard()

  // Handle recovered orders from abandoned orders screen
  // const handleAbandonedOrdersContinue = (orders: AbandonedOrder[]) => {
  //   setShowAbandonedOrdersScreen(false)
  //   // If there are recovered orders, we could navigate to payment or reload unpaid orders
  //   if (orders.length > 0) {
  //     loadUnpaidOrders()
  //   }
  // }

  // const handleAbandonedOrdersCancelAll = () => {
  //   setShowAbandonedOrdersScreen(false)
  //   loadUnpaidOrders()
  // }

  // Show abandoned orders screen first (only for logged-in users)
  // if (showAbandonedOrdersScreen && !loading) {
  //   return (
  //     <AbandonedOrdersScreen
  //       onContinue={handleAbandonedOrdersContinue}
  //       onCancelAll={handleAbandonedOrdersCancelAll}
  //     />
  //   )
  // }

  if (loading) {
    return (
      <div className={`min-h-screen bg-gradient-to-br ${palette.pageFrom} ${palette.pageVia} ${palette.pageTo} dark:from-gray-900 dark:via-gray-800 dark:to-gray-900`}>
        <Header title="Cargando..." />
        <LoadingSpinner message="Cargando información..." />
        <BottomNavigation tableId={routeId || ""} onOpenChatbot={handleCallWaiter} />
      </div>
    )
  }

  if (error) {
    return (
      <div className={`min-h-screen bg-gradient-to-br ${palette.pageFrom} ${palette.pageVia} ${palette.pageTo} dark:from-gray-900 dark:via-gray-800 dark:to-gray-900`}>
        <Header title="Error" />
        <ErrorMessage
          message={error}
          action={
            <button
              onClick={loadData}
              className={`bg-gradient-to-r ${palette.primaryFrom} ${palette.primaryTo} hover:${palette.primaryFromHover} hover:${palette.primaryToHover} text-white px-8 py-4 rounded-2xl transition-all duration-300 font-semibold shadow-lg hover:shadow-xl hover:scale-105`}
            >
              Reintentar
            </button>
          }
        />
        <BottomNavigation tableId={routeId || ""} onOpenChatbot={handleCallWaiter} />
      </div>
    )
  }

  const hasOrders = unpaidOrders && unpaidOrders.orders.length > 0
  const hasBalance = unpaidOrders && unpaidOrders.total_amount_owed > 0
  const pendingCollectionOrders = unpaidOrders?.orders.filter((o) => o.pending_collection) || []
  const otherOrders = unpaidOrders?.orders.filter((o) => !o.pending_collection) || []

  return (
    <div className={`min-h-screen bg-gradient-to-br ${palette.pageFrom} ${palette.pageVia} ${palette.pageTo} dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 pb-28`}>
      <Header
        title="ComandaYa"
        tableNumber={unpaidOrders?.table_number}
        tableStatus="Ocupada"
        showCallWaiter={!isTakeAway}
        onCallWaiter={handleCallWaiter}
        showServiceIndicator={true}
      />

      <div className={`px-4 py-6 space-y-6 max-w-md mx-auto ${hasOrders || hasBalance ? "pb-32" : ""}`}>
        <RestaurantStatusBanner
          isOpen={isOpen}
          config={config}
          isAccordionOpen={isAccordionOpen}
          setIsAccordionOpen={setIsAccordionOpen}
          orderTiming={orderTiming}
        />

        {!hasOrders && !hasBalance && !showScheduledSkeleton && (
          <DashboardHero
            config={config}
            unpaidOrders={unpaidOrders}
            offers={offers}
            showScheduledSkeleton={showScheduledSkeleton}
          />
        )}

        {showOrderSuccess && (
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border border-green-200/50 dark:border-green-800/50 rounded-2xl p-6 shadow-lg backdrop-blur-sm">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-emerald-500 rounded-2xl flex items-center justify-center mr-4 shadow-lg">
                <CheckCircle2 className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-green-800 dark:text-green-200 font-bold text-lg">¡Orden creada exitosamente!</h3>
                <p className="text-green-600 dark:text-green-400 text-sm">
                  {isTakeAway
                    ? "Tu pedido está en preparación para retiro. Te avisaremos cuando esté listo"
                    : "Tu pedido ha sido enviado a la cocina"}
                </p>
              </div>
            </div>
          </div>
        )}

        {!isTakeAway && waiterCalled && (
          <div
            onClick={() => setShowCancelWaiterModal(true)}
            className={`bg-gradient-to-r ${palette.pageVia} ${palette.pageTo} ${palette.borderAccent} rounded-2xl p-6 cursor-pointer transition-all duration-300 shadow-lg hover:shadow-xl backdrop-blur-sm`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className={`w-12 h-12 bg-gradient-to-r ${palette.primaryFrom} ${palette.primaryTo} rounded-2xl flex items-center justify-center mr-4 shadow-lg`}>
                  <Phone className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className={`${palette.textAccent} font-bold text-lg`}>Mozo en camino</h3>
                  <p className={`${palette.textAccent} text-sm`}>Toca para cancelar llamado</p>
                </div>
              </div>
              <div className="animate-pulse">
                <Phone className={`w-6 h-6 ${palette.textAccent}`} />
              </div>
            </div>
          </div>
        )}

        {(hasOrders || showScheduledSkeleton) && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                <Clock className="w-6 h-6 icon-primary" />
                Tus ordenes
              </h2>
              <button
                onClick={loadUnpaidOrders}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-all duration-200"
                title="Actualizar órdenes"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-600 dark:text-gray-400">
                  <path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8" />
                  <path d="M21 3v5h-5" />
                </svg>
              </button>
            </div>

            {!isSessionClosed && pendingCollectionOrders.length > 0 && (
              <>
                {isTakeAway ? (
                  <div className="space-y-4">
                    <div className="bg-yellow-50/80 dark:bg-stone-900/80 border border-yellow-200/50 dark:border-yellow-800/50 rounded-2xl p-4 backdrop-blur-sm shadow-sm">
                      <div className="text-yellow-800 dark:text-yellow-200 text-sm font-medium text-center">
                        {`Se te cobrara al entregarte ${pendingCollectionOrders.length > 1 ? "los siguientes pedidos" : "el siguiente pedido"}.`}
                      </div>
                    </div>
                    {pendingCollectionOrders.map((order) => (
                      <div key={order.uuid} className="shadow-lg rounded-2xl overflow-hidden">
                        <PendingCollectionOrderCard order={order} />
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="backdrop-blur-sm rounded-2xl shadow-lg transition-all duration-300 bg-yellow-50/80 dark:bg-stone-900/80 border border-yellow-200/50 dark:border-yellow-800/50 overflow-hidden">
                    <div
                      className="p-4 flex justify-between items-center cursor-pointer"
                      onClick={() => setIsAccordionOpen(!isAccordionOpen)}
                    >
                      <div className="text-yellow-800 dark:text-yellow-200 text-sm font-medium">
                        {`Alguien del personal vendrá a cobrar ${pendingCollectionOrders.length > 1 ? "estas órdenes" : "esta orden"}.`}
                      </div>
                    </div>

                    {isAccordionOpen && (
                      <div className="px-4 pb-4 max-h-60 overflow-y-auto">
                        <div className="space-y-4">
                          {pendingCollectionOrders.map((order) => (
                            <PendingCollectionOrderCard key={order.uuid} order={order} />
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}

            {showScheduledSkeleton && <OrderCardSkeleton palette={palette} />}
            {otherOrders.map((order) => (
              <OrderCard key={order.uuid} order={order} palette={palette} isTakeAway={!!isTakeAway} />
            ))}
          </div>
        )}

        <AccountSummary unpaidOrders={unpaidOrders} />
      </div>

      <DashboardActionButtons
        isSessionClosed={isSessionClosed}
        showScheduledSkeleton={showScheduledSkeleton}
        hasOrders={hasOrders}
        hasBalance={hasBalance}
        isTakeAway={!!isTakeAway}
        orderTiming={orderTiming}
        unpaidOrders={unpaidOrders}
        onOrderClick={handleOrderClick}
        onPaymentClick={handlePaymentClick}
      />

      {!isSessionClosed && (
        <BottomNavigation
          tableId={routeId || ""}
          onOpenChatbot={handleOpenChatbot}
          disabled={showScheduledSkeleton}
        />
      )}

      <DashboardModals
        showChatbot={showChatbot}
        setShowChatbot={setShowChatbot}
        showErrorModal={showErrorModal}
        setShowErrorModal={setShowErrorModal}
        errorModalMessage={errorModalMessage}
        isTakeAway={!!isTakeAway}
        showWaiterModal={showWaiterModal}
        setShowWaiterModal={setShowWaiterModal}
        confirmCallWaiter={confirmCallWaiter}
        showCancelWaiterModal={showCancelWaiterModal}
        setShowCancelWaiterModal={setShowCancelWaiterModal}
        handleCancelWaiter={handleCancelWaiter}
        showPendingCollectionOrderModal={showPendingCollectionOrderModal}
        setShowPendingCollectionOrderModal={setShowPendingCollectionOrderModal}
        confirmPendingCollectionOrder={confirmPendingCollectionOrder}
        showPendingCollectionPaymentModal={showPendingCollectionPaymentModal}
        setShowPendingCollectionPaymentModal={setShowPendingCollectionPaymentModal}
        isSessionClosed={isSessionClosed}
        isTakeAwaySessionClosed={isSessionClosed && !!isTakeAway}
      />
    </div>
  )
}
